import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
  CheckCircle, ExternalLink, Palette, ToggleLeft, Users,
  ArrowRight, Copy, Mail, Send, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePlatformStore } from '@/lib/store';
import { Spinner, PlanBadge } from '@/components/shared/UIComponents';
import { inviteTenantAdminViaEdge } from '@/lib/api';
import type { TenantPlan, BrandingConfig } from '@/types';

export function ProvisionSuccessPage() {
  const { tenantId } = useParams();
  const location = useLocation();
  const { selectedTenant: tenant, selectTenant, clearSelectedTenant } = usePlatformStore();

  // ── Owner email from wizard navigation state ─────────────────────────
  // ProvisionWizard passes the owner email when it redirects here so we
  // can pre-fill the Resend Invite block. Falls back to empty string when
  // the operator landed on this page via direct URL (e.g. a bookmark) so
  // they can still manually type the address.
  const passedOwnerEmail = (location.state as { ownerEmail?: string } | null)?.ownerEmail || '';
  const [ownerEmail, setOwnerEmail] = useState(passedOwnerEmail);
  const [resending, setResending] = useState(false);

  // ── DNS readiness for the tenant subdomain ───────────────────────────
  // provisionSubdomain (Cloudflare DNS record + Netlify domain alias) is
  // fire-and-forget inside createTenant. Without this poll, clicking
  // "Visit the App" too early lands on a Netlify 404 or NXDOMAIN — first
  // impression burned. We poll the subdomain with HEAD + no-cors: the
  // fetch resolves opaque on any server response (200/404/5xx — all mean
  // DNS + Netlify are wired) and rejects on network errors (DNS not yet
  // resolving). When it resolves we unblock the link. After ~60s we
  // unblock anyway in case the poll pattern is fooled — better to leak a
  // 404 than to block forever.
  const [dnsReady, setDnsReady] = useState(false);

  useEffect(() => {
    if (tenantId) selectTenant(tenantId);
    return () => clearSelectedTenant();
  }, [tenantId, selectTenant, clearSelectedTenant]);

  const subdomainUrl = tenant ? `https://${tenant.subdomain}` : '';

  useEffect(() => {
    if (!tenant?.subdomain) return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 20; // ~60s at 3s intervals

    const check = async () => {
      if (cancelled) return;
      attempts++;
      try {
        await fetch(subdomainUrl, { method: 'HEAD', mode: 'no-cors' });
        if (!cancelled) setDnsReady(true);
      } catch {
        if (cancelled) return;
        if (attempts < MAX_ATTEMPTS) {
          setTimeout(check, 3000);
        } else {
          // Fallback: unblock after the ceiling so the operator can still
          // try the link manually. A real-world DNS that isn't up after
          // 60s usually means a different problem (e.g. Cloudflare creds
          // wrong) — a 404 will tell us more than a permanent spinner.
          setDnsReady(true);
        }
      }
    };

    check();
    return () => { cancelled = true; };
  }, [tenant?.subdomain, subdomainUrl]);

  if (!tenant) {
    return <div className="flex justify-center py-32"><Spinner size="lg" /></div>;
  }

  const plan = tenant.plan as TenantPlan | undefined;
  const branding = tenant.branding as BrandingConfig | undefined;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  // ── Resend invite handler ────────────────────────────────────────────
  // Calls the same edge function (invite-tenant-admin) that createTenant
  // fire-and-forgets. We surface success/failure via toast — operator
  // hears immediately whether the resend landed, instead of having to
  // dig through console logs.
  const handleResendInvite = async () => {
    const email = ownerEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email address');
      return;
    }
    setResending(true);
    try {
      await inviteTenantAdminViaEdge(tenant.id, email, 'owner', tenant.name);
      toast.success(`Invite sent to ${email}`);
    } catch (err) {
      toast.error((err as Error)?.message || 'Failed to send invite');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 py-8">
      {/* Success header */}
      <div className="text-center animate-slide-up">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-500/10 mb-4">
          <CheckCircle size={32} className="text-emerald-600 dark:text-emerald-400" />
        </div>
        <h1 className="text-2xl font-bold text-surface-900">Tenant Created Successfully!</h1>
        <p className="text-surface-500 mt-2">
          <span className="text-surface-900 font-medium">{tenant.name}</span> is now live on the platform
        </p>
      </div>

      {/* Tenant card */}
      <div className="glass-panel p-6 animate-slide-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-surface-900 text-lg font-bold shrink-0"
            style={{ backgroundColor: branding?.primary_color || '#0d9488' }}>
            {tenant.name.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold text-surface-900">{tenant.name}</h2>
              <PlanBadge plan={plan?.name || 'free'} label={plan?.display_name} />
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-surface-500">Subdomain:</span>
              <code className="text-brand-600 dark:text-brand-400 font-mono text-xs bg-brand-500/10 px-2 py-0.5 rounded">
                {tenant.subdomain}
              </code>
              <button onClick={() => copyToClipboard(subdomainUrl)}
                className="p-1 rounded hover:bg-surface-100 text-surface-500 hover:text-surface-900 transition-colors"
                title="Copy URL">
                <Copy size={12} />
              </button>
            </div>
            <div className="text-xs text-surface-500 mt-1">
              14-day trial started · {plan?.max_users || '∞'} max users · {plan?.max_storage_mb ? `${plan.max_storage_mb >= 1024 ? `${plan.max_storage_mb / 1024} GB` : `${plan.max_storage_mb} MB`}` : ''} storage
            </div>
          </div>
        </div>
      </div>

      {/* Owner Invite block — recovery path for a failed (or
          undeliverable) first invite. createTenant fires the invite
          as fire-and-forget; without this UI the operator has no way
          to know whether it landed, and the tester can be locked out
          of their tenant admin from minute one. */}
      <div className="glass-panel p-5 animate-slide-up" style={{ animationDelay: '150ms', animationFillMode: 'forwards' }}>
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400">
            <Mail size={18} />
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-surface-900">Owner invite</div>
            <div className="text-xs text-surface-500">
              {passedOwnerEmail
                ? `An invite has been sent to ${passedOwnerEmail}. If they don't receive it within 2 minutes, resend below.`
                : 'No owner email was provided during provisioning. Enter one now to send the first invite.'}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <input
            type="email"
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            placeholder="owner@example.com"
            className="input-field flex-1"
          />
          <button
            onClick={handleResendInvite}
            disabled={resending || !ownerEmail.trim()}
            className="btn-primary text-sm flex items-center gap-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {resending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {resending ? 'Sending…' : 'Resend invite'}
          </button>
        </div>
      </div>

      {/* Next steps */}
      <div className="space-y-3 animate-slide-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
        <h3 className="text-sm font-semibold text-surface-900">Recommended Next Steps</h3>

        <Link to={`/admin/tenants/${tenant.id}/manage/branding`}
          className="flex items-center gap-4 p-4 glass-panel hover:border-surface-400 transition-all group">
          <div className="p-2 rounded-xl badge-violet"><Palette size={18} /></div>
          <div className="flex-1">
            <div className="text-sm font-medium text-surface-900 group-hover:text-brand-600 dark:text-brand-400 transition-colors">Customise Branding</div>
            <div className="text-xs text-surface-500">Upload logo, set colours, configure fonts and welcome message</div>
          </div>
          <ArrowRight size={16} className="text-surface-400 group-hover:text-surface-500 transition-colors" />
        </Link>

        <Link to={`/admin/tenants/${tenant.id}/manage/features`}
          className="flex items-center gap-4 p-4 glass-panel hover:border-surface-400 transition-all group">
          <div className="p-2 rounded-xl badge-blue"><ToggleLeft size={18} /></div>
          <div className="flex-1">
            <div className="text-sm font-medium text-surface-900 group-hover:text-brand-600 dark:text-brand-400 transition-colors">Configure Features</div>
            <div className="text-xs text-surface-500">Toggle which features are enabled for this tenant's users</div>
          </div>
          <ArrowRight size={16} className="text-surface-400 group-hover:text-surface-500 transition-colors" />
        </Link>

        <Link to={`/admin/tenants/${tenant.id}/manage/users`}
          className="flex items-center gap-4 p-4 glass-panel hover:border-surface-400 transition-all group">
          <div className="p-2 rounded-xl badge-emerald"><Users size={18} /></div>
          <div className="flex-1">
            <div className="text-sm font-medium text-surface-900 group-hover:text-brand-600 dark:text-brand-400 transition-colors">Invite Users</div>
            <div className="text-xs text-surface-500">Add the first admin and end-users to this tenant</div>
          </div>
          <ArrowRight size={16} className="text-surface-400 group-hover:text-surface-500 transition-colors" />
        </Link>

        {/* "Visit the App" — gated on DNS readiness. Until the subdomain
            resolves, render as a non-clickable "Provisioning subdomain…"
            tile. Prevents operators (and testers) from hitting a 404 on
            first click and losing trust before they see the product. */}
        {dnsReady ? (
          <a href={subdomainUrl} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 glass-panel hover:border-surface-400 transition-all group">
            <div className="p-2 rounded-xl badge-brand"><ExternalLink size={18} /></div>
            <div className="flex-1">
              <div className="text-sm font-medium text-surface-900 group-hover:text-brand-600 dark:text-brand-400 transition-colors">Visit the App</div>
              <div className="text-xs text-surface-500">See how the tenant's app looks at {tenant.subdomain}</div>
            </div>
            <ArrowRight size={16} className="text-surface-400 group-hover:text-surface-500 transition-colors" />
          </a>
        ) : (
          <div className="flex items-center gap-4 p-4 glass-panel opacity-60 cursor-wait">
            <div className="p-2 rounded-xl badge-brand">
              <Loader2 size={18} className="animate-spin" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium text-surface-900">Provisioning subdomain…</div>
              <div className="text-xs text-surface-500">
                DNS for {tenant.subdomain} is being set up. This usually takes 30 seconds.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer links */}
      <div className="flex items-center justify-center gap-4 pt-4">
        <Link to={`/admin/tenants/${tenant.id}`} className="btn-secondary text-sm">
          View Tenant Details
        </Link>
        <Link to="/admin/tenants" className="btn-ghost text-sm">
          Back to All Tenants
        </Link>
      </div>
    </div>
  );
}
